import Image from 'next/image';
import { useMemo } from 'react';
import { NetworkData, NETWORKS } from 'src/common/types/network';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { ArgumentDisplay } from './components/ArgumentDisplay';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

const SUBGRAPHS = [
  { chainId: 1, label: 'Resolv Convex LP', value: 'Resolv_Convex' },
  { chainId: 1, label: 'Resolv USR - Level: XP Farm', value: 'Resolv-LRP' },
  { chainId: 1, label: 'Resolv LRP - Level: XP Farm', value: 'Resolv-USR' },
  { chainId: 1, label: 'Morpho wstUSR', value: 'Morpho_WstUsr' },
  { chainId: 34443, label: 'Ironclad staking', value: 'Ironclad_Staking' },
  { chainId: 80084, label: 'BeraBorrow Positions', value: 'BeraBorrow_SharedPositions' },
  { chainId: 80084, label: 'BeraBorrow Collateral Positions', value: 'BeraBorrow_CollateralPositions' },
  { chainId: 80084, label: 'BeraBorrow Collateral Tokens', value: 'BeraBorrow_CollateralTokens' },
  { chainId: 42161, label: 'Vertex Stake', value: 'Vertex_Stake' },
  { chainId: 80094, label: 'BeraBorrow Honey-NECT Kodak Island', value: 'BeraBorrow-Honey-Nect-Kodak-Island' },
  { chainId: 80094, label: 'BeraBorrow USDe-NECT Kodak Island', value: 'BeraBorrow-USDe-Nect-Kodak-Island' },
  { chainId: 80094, label: 'Infrared IBGT Stakes', value: 'Infrared_IBGT_Stakes' },
  {
    chainId: 1,
    label: 'Morpho supply csUSDL collateral to borrow USDC',
    value: 'MorphoSupplyCsusdlCollateralToBorrowUsdc',
  },
  {
    chainId: 1,
    label: 'Trade on Gearbox',
    value: 'GEARBOX_TRADE',
  },
  {
    chainId: 1,
    label: 'Trade on Contango',
    value: 'CONTANGO_TRADE',
  },
];

export function SubgraphTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const subgraphName = useMemo(() => {
    // Use context (new approach)
    if (trigger.context?.subgraphName) {
      return trigger.context.subgraphName;
    }

    // @deprecated - Fallback to signature extraction for legacy triggers
    if (trigger.signature) {
      const match = trigger.signature.match(/^SubgraphBalance-(.+)$/);
      return match ? match[1] : '';
    }

    return '';
  }, [trigger.context?.subgraphName, trigger.signature]);

  const tokenCurrency = useMemo(() => {
    return trigger.context?.tokenCurrency ?? '';
  }, [trigger.context?.tokenCurrency]);

  const conditionExpression = useMemo(() => {
    return trigger.context?.condition_expression ?? trigger.conditionExpression ?? '-';
  }, [trigger.context?.condition_expression, trigger.conditionExpression]);

  const networkDisplay = useMemo(() => {
    const subgraph = SUBGRAPHS.find((s) => s.value === subgraphName);
    if (!subgraph) return '-';

    const network = NETWORKS.find((n: NetworkData) => n.chainId === subgraph.chainId);
    if (!network) return '-';

    const chainIconUrl = getChainIconUrl(subgraph.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={network.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{network.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [subgraphName, getChainIconUrl]);

  const volumeCurrencyDisplay = useMemo(() => {
    const currencyToUse = tokenCurrency || trigger.currencyVolumeExpression;
    if (!currencyToUse) return '-';

    const subgraph = SUBGRAPHS.find((s) => s.value === subgraphName);
    const chainId = subgraph?.chainId;

    return <CurrencyDisplay value={currencyToUse} chainId={chainId} />;
  }, [tokenCurrency, trigger.currencyVolumeExpression, subgraphName]);

  const executedWithDisplay = useMemo(() => {
    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of
        </Text>
        <ArgumentDisplay value={subgraphName} suffix="subgraph" />
      </div>
    );
  }, [subgraphName]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={executedWithDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={conditionExpression} />
    </KeyValueTable>
  );
}
